import { create } from 'zustand';

/**
 * D-130 Phase 6C — the location the customer is currently looking at.
 *
 * This is a VIEW selection, never an authorization decision. The server decides
 * what the identity may see; this store only remembers which of the
 * server-offered options is selected so every list, count and dashboard query
 * asks for the same thing.
 *
 * Deliberately NOT persisted:
 *   - a stale selection could survive a staff-side scope change and produce a
 *     confusing 404 on the next visit;
 *   - the authorised set arrives fresh from `/me` on every session, so there is
 *     nothing safe to remember across one.
 *
 * `null` means "everything this identity is entitled to" — for a LOCATION
 * contact that is its own site (the server scopes it), for an ORGANIZATION
 * contact it is All locations.
 */
export const UNASSIGNED_LOCATION = 'unassigned' as const;

/** The selected value: a location id, the explicit Unassigned bucket, or null = all. */
export type CustomerLocationSelection = string | typeof UNASSIGNED_LOCATION | null;

interface CustomerScopeState {
  selectedLocationId: CustomerLocationSelection;
  setSelectedLocation: (value: CustomerLocationSelection) => void;
  reset: () => void;
}

export const useCustomerScopeStore = create<CustomerScopeState>((set) => ({
  selectedLocationId: null,
  setSelectedLocation: (value) => set({ selectedLocationId: value }),
  reset: () => set({ selectedLocationId: null }),
}));

/**
 * The value to send as `locationId`.
 *
 * A LOCATION-scoped contact NEVER sends one: the server already confines it to
 * its own site, and sending a value would be the frontend pretending to make an
 * authorization choice. Returning `undefined` here is what keeps the UI honest.
 */
export function locationParamFor(
  scope: 'LOCATION' | 'ORGANIZATION' | undefined,
  selection: CustomerLocationSelection,
): string | undefined {
  if (scope !== 'ORGANIZATION') return undefined;
  return selection ?? undefined;
}
